import EventDispatcher from '../events/EventDispatcher';
import Timer from '../Timer';
import Tween from './Tween';

/**
 * @class
 * @classdesc Класс анимационных последовтельностей
 * @extends module:events.EventDispatcher
 * @inheritDoc
 * @memberof module:animation
 * @example let sequence = [
 * {
     *     tweens: [
     *         {prop: "x", to: 100},
     *         {prop: "y", to: 100},
     *         {prop: "rotation", to: Math.PI*2, duration: 1000}
     *     ],
     *     duration: 800,
     *     ease: easing.cubic.easeIn
     * },
     * {
     *     tweens: [
     *         {prop: "scale.x", from: 0.5, to: 2},
     *         {prop: "scale.y", from: 0.5, to: 2},
     *         {prop: "rotation", to: 0}
     *     ],
     *     duration: 1000
     * }
 * ];
 */
class Sequence extends EventDispatcher {

    /**
     * @constructor
     * @param {Object} obj ссылка на объект, к которому будут применяться анимационные последовтельности
     * @param {Object} sequence конфигурация последовательностей
     */
    constructor(obj, sequence)
    {
        super();

        this.obj = obj;
        this.sequence = sequence;
        this.currentAnimation = -1;

        this.currentTweens = [];

        this.ended = false;

        this.startTimer = null;
        this.endTimer = null;
    }

    /**
     * Запуск последовтельностей
     */
    play() {
        this.playNext();
    }

    /**
     * Старт следующей анимации
     */
    playNext() {

        if(this.currentAnimation >= 0) {
            var oldAnimation = this.sequence[this.currentAnimation];
            if(oldAnimation.onfinish instanceof Function) oldAnimation.onfinish({target: this});
            if(typeof oldAnimation.loop != "undefined" && oldAnimation.loop >= 0) this.currentAnimation = oldAnimation.loop - 1;
        }

        this.currentAnimation++;
        if(this.currentAnimation >= this.sequence.length) {
            this.ended = true;
            /**
             * Окончание анимации
             * @event module:animation.Sequence#finish
             */
            this.emit("finish");
            return;
        }

        var animation = this.sequence[this.currentAnimation];
        var masterDuration, duration, tweens, tween, from, to, t, ease;

        tweens = animation.tweens;
        if(!Array.isArray(tweens)) tweens = [tweens];

        masterDuration = animation.duration || 0;

        this.currentTweens = [];

        var finishTween = null;

        for(var n=0; n < tweens.length; n++) {

            tween = tweens[n];

            let obj = this.obj;
            let prop = tween.prop;
            let parts = prop.split(".");
            while(parts.length > 1) obj = obj[parts.shift()];
            prop = parts[0];

            duration = tween.duration;
            if (typeof duration == 'undefined') duration = masterDuration;

            from = tween.from;
            if (typeof from == 'undefined') from = obj[prop];

            to = tween.to;
            if (typeof to == 'undefined') to = obj[prop];

            ease = tween.ease || animation.ease || null;

            t = new Tween(obj, prop, from, to, duration, ease);

            if(typeof tween.onchange != "undefined") t.on('change', tween.onchange);
            if(typeof tween.onfinish != "undefined") t.on('finish', tween.onfinish);

            t.play();

            this.currentTweens.push(t);

            if (duration == masterDuration) {
                finishTween = t;
            }
        }

        this.endTimer = new Timer(() => this.playNext(), masterDuration);
    }

    /**
     * @ignore
     */
    executeTweensMethod(method) {
        for(let i=0; i<this.currentTweens.length; i++) {
            this.currentTweens[i][method]();
        }
    }

    /**
     * @ignore
     */
    clearTweens() {
        for(let i=0; i<this.currentTweens.length; i++) {
            this.currentTweens[i].destroy = true;
        }

        this.currentTweens = [];
    }

    /**
     * Полная остановка последовательности
     */
    stop() {
        if(this.ended) return;

        this.executeTweensMethod("stop");
        if(this.startTimer) this.startTimer.destroy = true;
        if(this.endTimer) this.endTimer.destroy = true;
    }

    /**
     * Пауза проигрывания
     */
    pause() {
        if(this.ended) return;

        this.executeTweensMethod("pause");
        if(this.startTimer) this.startTimer.pause();
        if(this.endTimer) this.endTimer.pause();
    }

    /**
     * Возобновление проигрывания
     */
    resume() {
        if(this.ended) return;

        this.executeTweensMethod("play");
        if(this.startTimer) this.startTimer.resume();
        if(this.endTimer) this.endTimer.resume();
    }

    /**
     * Инициализация и запуск последовтельности анимаций
     * @param {Object} obj ссылка на объект, к которому будут применяться анимационные последовтельности
     * @param {Object} sequence конфигурация последовательностей
     * @param {Number} [delay=0] задержка перед началом проигрывания
     * @returns {Sequence}
     * @example Sequence.start(mySprite, [
     * {
     *     tweens: [
     *         {prop: "x", to: 100},
     *         {prop: "y", to: 100},
     *         {prop: "rotation", to: Math.PI*2, duration: 1000}
     *     ],
     *     duration: 800,
     *     ease: easing.cubic.easeIn
     * },
     * {
     *     tweens: [
     *         {prop: "scaleX", from: 0.5, to: 2},
     *         {prop: "scaleY", from: 0.5, to: 2},
     *         {prop: "rotation", to: 0}
     *     ],
     *     duration: 1000
     * }
     * ]);
     */
    static start(obj, sequence, delay) {
        if(!obj || !sequence) return;

        var anim = new Sequence(obj, sequence);

        if(delay) anim.startTimer = new Timer(() => anim.play(), delay);
        else anim.play();

        return anim;
    }
}

export default Sequence;